import { type Icon, IconCheck, IconCopy } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import {
	Alert,
	Button,
	Empty,
	Flex,
	Grid,
	Skeleton,
	Space,
	type SpaceProps,
	Spin,
	Tooltip,
	Typography,
	theme,
} from "antd";
import type React from "react";
import {
	ERROR_NETWORK_DESC,
	ERROR_NETWORK_TITLE,
	INFO_NOT_FOUND_DESC,
	INFO_NOT_FOUND_TITLE,
} from "@/common/messages";
import type { LinkType } from "@/common/model";
import { formatLocalDateTime, formatUtcDateTime } from "@/common/utils";
import { createLabelTag } from "./status-pill";

export const LinkTypeLabelMap: Record<LinkType, string> = {
	SOURCE_CODE: "Source code",
	TOOL: "Tool",
	DOCUMENTATION: "Documentation",
	GENERIC: "Generic",
};

export const LinkTypeBadge = createLabelTag(LinkTypeLabelMap);

/** App-default Typography `copyable` config: tabler icons instead of antd's
 *  defaults (antd has no theme-level icon override). Spread and extend:
 *  `copyable={{ ...COPYABLE, text: value }}`. */
export const COPYABLE = {
	icon: [
		<IconCopy key="copy" size={14} stroke={2} />,
		<IconCheck key="copied" size={14} stroke={2} />,
	],
};

/** Renders a Tabler icon sized for use inside antd Buttons, inheriting color from context. */
export function CustomIcon({
	icon: IconComponent,
	tooltip,
	disabled,
	...props
}: {
	icon: React.ComponentType<React.ComponentPropsWithoutRef<Icon>>;
	tooltip?: string;
	disabled?: boolean;
} & Partial<React.ComponentPropsWithoutRef<Icon>>) {
	const base = <IconComponent size={20} stroke={1.8} {...props} />;

	// ponytail: span only exists for disabled — avoids baseline misalignment in the normal path
	const icon = disabled ? (
		<span style={{ lineHeight: 0, filter: "grayscale(1) opacity(0.3)" }}>
			{base}
		</span>
	) : (
		base
	);

	if (tooltip) return <Tooltip title={tooltip}>{icon}</Tooltip>;
	return icon;
}

/**
 * Shows a date in local time (always with hh:mm) with the UTC instant in a tooltip.
 */
export function DateField({ value }: { value?: string | Date | null }) {
	if (!value) return <>-</>;
	return (
		<Tooltip title={formatUtcDateTime(value)}>
			<Typography.Text ellipsis>{formatLocalDateTime(value)}</Typography.Text>
		</Tooltip>
	);
}

/**
 * Show the ID of an entity
 */
export function IdField({
	id,
	link,
	showCopyButton,
}: {
	id?: string;
	link?: string;
	showCopyButton?: boolean;
}) {
	if (!id) return <>-</>;
	if (link)
		return (
			<Link
				style={{
					maxWidth: 200,
				}}
				to={link}
				onClick={(e) => e.stopPropagation()}
			>
				{id}
			</Link>
		);
	return (
		// <Tag>
		<Typography.Text
			copyable={showCopyButton ? { ...COPYABLE, text: id } : false}
			ellipsis={{ tooltip: id }}
			style={{
				maxWidth: 220,
			}}
		>
			{id}
		</Typography.Text>
		// </Tag>
	);
}

export type BaseContainerProps = Omit<SpaceProps, "size"> & {
	children: React.ReactNode;
	title?: string;
	/** Title size; use "small" for sections nested inside a drawer. */
	size?: "default" | "small";
	description?: string;
	actions?: React.ReactNode;
	padding?: number;
	dark?: boolean;
	/** Block interaction with the content and dim it (e.g. during an in-flight action). */
	disabled?: boolean;
};

export function BaseContainer({
	children,
	title,
	description,
	dark = false,
	actions,
	padding = 0,
	disabled = false,
	size = "default",
	...props
}: BaseContainerProps) {
	const screens = Grid.useBreakpoint();
	const isMobile = !screens.md;

	const {
		token: { borderRadius, colorBgContainerDisabled },
	} = theme.useToken();

	return (
		<Flex
			{...props}
			orientation={props.orientation ?? "vertical"}
			style={{
				backgroundColor: "transparent",
				padding: 0,
				height: "100%",
				width: "100%",
				alignSelf: "center",
				gap: 15,
				...props.style,
			}}
		>
			{title && (
				<Flex justify="space-between" align="center" wrap gap={8}>
					{/* Title and description */}
					<Space orientation="vertical" style={{ gap: 0 }}>
						{title && (
							<Typography.Title
								level={size === "small" ? 5 : 4}
								style={{ margin: 0 }}
							>
								{title}
							</Typography.Title>
						)}
						{description && (
							<Typography.Text style={{ margin: 0, opacity: 0.8 }}>
								{description}
							</Typography.Text>
						)}
					</Space>
					{/* Right actions */}
					<Space
						orientation="horizontal"
						style={{
							flex: 1,
							width: isMobile ? "100%" : undefined,
							alignItems: "center",
							justifyContent: "flex-end",
							display: "flex",
							flexWrap: "wrap",
						}}
					>
						{actions}
					</Space>
				</Flex>
			)}
			{children && (
				<Flex
					vertical={props.orientation !== "horizontal"}
					inert={disabled || undefined}
					style={{
						flex: 1,
						width: "100%",
						gap: 8,
						padding: 0,
						background: dark ? colorBgContainerDisabled : "transparent",
						borderRadius,
						opacity: disabled ? 0.5 : 1,
						transition: "opacity 0.2s",
					}}
				>
					{children}
				</Flex>
			)}
		</Flex>
	);
}

/** Handles pending/error states for a query; renders children when data is ready. */
export function QueryGuard({
	isPending,
	isError,
	isNull,
	children,
	title,
	message,
	pending,
}: {
	isPending: boolean;
	isError: boolean;
	isNull?: boolean;
	children: React.ReactNode;
	title?: string;
	message?: string;
	/** Custom loading node; defaults to a full-screen spinner. */
	pending?: React.ReactNode;
}) {
	if (isPending) return <>{pending ?? <Skeleton active />}</>;
	if (isError)
		return (
			<FullAlert
				type="error"
				title={title || ERROR_NETWORK_TITLE}
				message={message || ERROR_NETWORK_DESC}
			/>
		);
	if (isNull)
		return (
			<FullAlert
				type="info"
				title={title || INFO_NOT_FOUND_TITLE}
				message={message || INFO_NOT_FOUND_DESC}
			/>
		);

	return <>{children}</>;
}

/** Renders a full-screen spinning loader. */
export function FullSpin({
	description,
	size = "large",
}: {
	description?: string;
	size?: "small" | "large" | "medium";
}) {
	return (
		<Flex
			style={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				flexDirection: "column",
				gap: 16,
				height: "100%",
				width: "100%",
			}}
		>
			<Spin size={size} />
			{description && (
				<Typography.Text type="secondary">{description}</Typography.Text>
			)}
		</Flex>
	);
}

/** Renders a full-screen message. */
export function FullAlert({
	message,
	title,
	type = "error",
}: {
	message?: string;
	title?: string;
	type?: "error" | "warning" | "info" | "success";
}) {
	return (
		<Flex
			style={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				height: "100%",
				width: "100%",
			}}
		>
			<Alert
				type={type}
				title={title ?? "Error"}
				description={message ?? "An error occurred"}
			/>
		</Flex>
	);
}

/** Wraps the page content that adjusts padding based on the screen size. */
export function BasePageContainer({ children }: { children: React.ReactNode }) {
	const { useBreakpoint } = Grid;
	const screens = useBreakpoint();
	const padding = screens.md ? 2 : 8;
	return (
		<Space
			vertical
			size={"medium"}
			style={{
				padding,
				maxWidth: 1600,
				width: "100%",
			}}
		>
			{children}
		</Space>
	);
}

export function CustomEmpty({
	description,
	expanded = false,
	onAdd,
	children,
	...props
}: {
	expanded?: boolean;
	description: string;
	/** When provided, renders an "Add" link after the message so the caller can open a create dialog. */
	onAdd?: () => void;
	children?: React.ReactNode;
	props?: React.ComponentProps<typeof Empty>;
}) {
	const {
		token: { colorBorder, borderRadius },
	} = theme.useToken();
	const emptyDescription = onAdd ? (
		<Flex align="center" justify="space-between" gap={6} wrap>
			<span>{`No ${description.toLowerCase()}.`}</span>
			<Button
				type="link"
				size="small"
				onClick={onAdd}
				style={{ padding: 0, height: "auto" }}
			>
				Add
			</Button>
		</Flex>
	) : (
		`No ${description.toLowerCase()}`
	);
	if (expanded)
		return (
			<Empty description={emptyDescription} {...props}>
				{children}
			</Empty>
		);
	return (
		<Empty
			description={emptyDescription}
			styles={{ image: { display: "none" } }}
			style={{
				border: `1px dashed ${colorBorder}`,
				textAlign: "start",
				borderRadius,
				padding: "10px 14px",
			}}
			{...props}
		></Empty>
	);
}
